import { all, call, put, select, takeEvery, race, take, cancelled } from 'redux-saga/effects';
import { currentStateSelector, conversationIdSelector } from '../store/translatorSelectors.ts';
import type { AxiosResponse } from 'axios';
import { TranslatorActions, userSpeechTranscribed } from './translatorActions.ts';
import type { TranslationRequest, TranslationControllerTranslatedAudioResult } from '@/api-gen/api-v3.schemas';
import { translationTranslateTo } from '@/api-gen/api-v3';
import { AudioChatActions } from 'app/store/AudioChat/audioChatActions.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import i18n from '@/i18n/i18n.ts';
import {
  sagaSourceLanguageCode,
  sagaTargetLanguageCode,
  selectedSourceLanguageCode,
  selectedTargetLanguageCode,
  setSagaLanguages,
} from '@/features/translator/index.ts';

// Helper to decode base64 and play audio
function* playBase64Audio(base64Data: string) {
  const audioContext = new AudioContext();
  let source: AudioBufferSourceNode | null = null;

  try {
    const audioBinary = yield call([null, decodeBase64], base64Data);
    const audioBuffer = yield call([audioContext, audioContext.decodeAudioData], audioBinary);

    source = audioContext.createBufferSource();
    if (source) {
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start();

      // Wait for the audio to finish
      yield new Promise<void>((resolve) => {
        if (source) {
          source.onended = () => {
            resolve();
          };
        } else {
          resolve();
        }
      });
    }
  } catch (err) {
    console.error('Failed to play base64 audio', err);
  } finally {
    // If this generator is canceled (e.g. from a race), stop the source
    if ((yield cancelled()) && source) {
      try {
        source.stop();
      } catch (e) {
        console.error('Error stopping audio source', e);
      }
    }
    audioContext.close();
  }
}

function decodeBase64(base64String: string): ArrayBuffer {
  const binaryString = atob(base64String);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function* handleUserSpeechTranscribed(action: ReturnType<typeof userSpeechTranscribed>) {
  try {
    const { transcriptions } = action.payload;

    // Early return if transcriptions array is empty, but ensure state transitions
    if (!transcriptions || transcriptions.length === 0) {
      // Reset state back to Idle to allow the conversation to continue
      yield put(AudioChatActions.translatedSpeechFinished());
      return;
    }

    // 1) Add only the "auto" language user message to transcript
    const userTranscription = transcriptions[0];
    if (userTranscription) {
      yield put(
        AudioChatActions.addTranscriptMessage({
          text: userTranscription.text,
          isUser: true,
          date: new Date(),
          name: i18n.t(`${I18nNamespace.Translator}:setup.you`)
        })
      );
    }

    const fromLanguage = sagaSourceLanguageCode.value || selectedSourceLanguageCode.value;
    const toLanguage = sagaTargetLanguageCode.value || selectedTargetLanguageCode.value;
    const conversationId = yield select(conversationIdSelector);

    const request: TranslationRequest = {
     transcription: transcriptions[0].text,
     fromLanguage: fromLanguage,
     toLanguage: toLanguage,
     conversationId: conversationId
    };

    // 3) Translate
    //    returns { audio?: string, translation?: string }
    const response: AxiosResponse<TranslationControllerTranslatedAudioResult> = yield call(
      translationTranslateTo,
      request
    );

    const { audio, translation, transcription } = response.data;

    // Then post the AI message
    yield put(
      AudioChatActions.addTranscriptMessage({
        text: translation || '',
        isUser: false,
        date: new Date(),
        name: i18n.t(`${I18nNamespace.Translator}:setup.translator`)
      })
    );

    // 5) Mark that AI is "Speaking"
    yield put(AudioChatActions.translatedSpeechReady());

    // 6) If still active, play returned audio (if any)
    const translatorCurrentState = yield select(currentStateSelector);
    if (translatorCurrentState === 'active' && audio) {
      yield race({
        done: call(playBase64Audio, audio),
        canceled: take(TranslatorActions.endConversation.type)
      });
    }

    // 7) Mark "Speaking" done
    yield put(AudioChatActions.translatedSpeechFinished());
  } catch (error) {
    console.error('Translation saga error', error);
    // Ensure state is reset even on error
    yield put(AudioChatActions.translatedSpeechFinished());
  }
}

function* resetTranscriptSaga() {
  // Reset both UI and Saga states
  yield call(setSagaLanguages, '', '');
  yield put(AudioChatActions.resetTranscript());
}

export default function* translatorSaga() {
  yield all([
    takeEvery(userSpeechTranscribed.type, handleUserSpeechTranscribed),
    takeEvery(TranslatorActions.startConversation, resetTranscriptSaga)
  ]);
}
